
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type {
  CmsArticle,
  CmsCategory,
  SiteContent,
  SiteConductBlock,
  SiteHero,
  SiteRulesSection,
  SiteStaffMember,
  SiteStaffXatRank,
} from '@/lib/cms-types';
import { mergeCategoriesWithHomeDefaults, HELP_CENTER_HOME_CATEGORY_IDS } from '@/lib/help-center-home-categories';
import { mergeSiteWithDefaults } from '@/lib/site-defaults';
import { readCachedSiteContent, writeCachedSiteContent } from '@/lib/site-content-cache';
import * as guidesService from '@/services/guides.service';
import * as siteContentService from '@/services/site-content.service';
import { ApiError, hasApiAuthToken } from '@/services/api-client';
import { toast } from 'sonner';
import { analytics, AnalyticsEventNames } from '@/services/analytics';
import { prefetchStaffOnlineStatuses } from '@/lib/staff-online-status';

type CmsState = {
  categories: CmsCategory[];
  publicArticles: CmsArticle[];
  adminArticles: CmsArticle[];
  trashedArticles: CmsArticle[];
  site: SiteContent;
  /** sourceRefs de guias estáticos que o utilizador eliminou — não voltam a ser gerados no seed. */
  staticArticleExclusions: string[];
};

type CmsContextValue = Omit<CmsState, 'publicArticles' | 'adminArticles' | 'trashedArticles'> & {
  articles: CmsArticle[];
  trashedArticles: CmsArticle[];
  hydrated: boolean;
  /**
   * True quando o site CMS já pode ser mostrado no público:
   * cache da sessão ou fetch remoto concluído (nunca o seed hardcoded sozinho).
   */
  siteReady: boolean;
  /** No painel: true após carregar a lista admin (evita “não encontrado” prematuro). */
  adminHydrated: boolean;
  addCategory: (input: Omit<CmsCategory, 'id'>) => CmsCategory;
  updateCategory: (id: string, patch: Partial<Omit<CmsCategory, 'id'>>) => void;
  deleteCategory: (id: string) => void;
  addArticle: (
    input: Omit<CmsArticle, 'id' | 'createdAt' | 'updatedAt'>
  ) => Promise<CmsArticle>;
  updateArticle: (
    id: string,
    patch: Partial<Omit<CmsArticle, 'id' | 'createdAt'>>
  ) => Promise<void>;
  deleteArticle: (id: string) => void;
  restoreArticle: (id: string) => void;
  permanentlyDeleteArticle: (id: string) => void;
  getCategoryById: (id: string) => CmsCategory | undefined;
  getArticleById: (id: string) => CmsArticle | undefined;
  setSite: (site: SiteContent, options?: { persist?: boolean }) => void;
  patchSiteHero: (patch: Partial<SiteHero>) => void;
  setRulesSections: (sections: SiteRulesSection[]) => void;
  setConduct: (conduct: SiteConductBlock) => void;
  setStaffMembers: (members: SiteStaffMember[]) => void;
  addStaffMember: (input: Omit<SiteStaffMember, 'id'>) => SiteStaffMember;
  updateStaffMember: (
    id: string,
    patch: Partial<Omit<SiteStaffMember, 'id'>> & { xatRank?: SiteStaffXatRank | null }
  ) => void;
  deleteStaffMember: (id: string) => void;
};

const CmsContext = createContext<CmsContextValue | null>(null);

function nowIso() {
  return new Date().toISOString();
}

function emptyState(initialSite?: SiteContent | null): CmsState {
  return {
    categories: [],
    publicArticles: [],
    adminArticles: [],
    trashedArticles: [],
    site: initialSite ?? mergeSiteWithDefaults(null),
    staticArticleExclusions: [],
  };
}

function syncPublishedArticle(list: CmsArticle[], article: CmsArticle): CmsArticle[] {
  const without = list.filter((item) => item.id !== article.id);
  if (article.status !== 'published') return without;
  return [...without, article];
}

/** Evita que um refetch lento apague edições otimistas mais recentes. */
function mergePublicArticlesPreferNewer(remote: CmsArticle[], local: CmsArticle[]): CmsArticle[] {
  const byId = new Map(remote.map((a) => [a.id, a]));
  for (const article of local) {
    const current = byId.get(article.id);
    if (!current) {
      if (article.status === 'published') byId.set(article.id, article);
      continue;
    }
    const localTs = Date.parse(article.updatedAt) || 0;
    const remoteTs = Date.parse(current.updatedAt) || 0;
    if (localTs >= remoteTs) byId.set(article.id, article);
  }
  return Array.from(byId.values());
}

function mergeAdminArticlesPreferNewer(remote: CmsArticle[], local: CmsArticle[]): CmsArticle[] {
  const byId = new Map(remote.map((a) => [a.id, a]));
  for (const article of local) {
    // ids temporários de create ainda não confirmado
    if (article.id.length > 20 && !byId.has(article.id)) {
      byId.set(article.id, article);
      continue;
    }
    const current = byId.get(article.id);
    if (!current) continue;
    const localTs = Date.parse(article.updatedAt) || 0;
    const remoteTs = Date.parse(current.updatedAt) || 0;
    if (localTs >= remoteTs) byId.set(article.id, article);
  }
  return Array.from(byId.values());
}

async function loadRemoteState(): Promise<Omit<CmsState, 'adminArticles' | 'trashedArticles'>> {
  const [categories, publicArticles, site] = await Promise.all([
    guidesService.getCategories(),
    guidesService.getPublicGuides(),
    siteContentService.getSiteContent(),
  ]);
  return {
    categories: mergeCategoriesWithHomeDefaults(categories),
    publicArticles,
    site: mergeSiteWithDefaults(site),
    staticArticleExclusions: [],
  };
}

export function CmsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isPainelRoute = location.pathname.startsWith('/painel');
  const cachedSiteRef = useRef<SiteContent | null>(readCachedSiteContent());
  const [state, setState] = useState<CmsState>(() => emptyState(cachedSiteRef.current));
  const [hydrated, setHydrated] = useState(false);
  const [siteReady, setSiteReady] = useState(() => cachedSiteRef.current !== null);
  const [adminHydrated, setAdminHydrated] = useState(false);
  const adminReadyRef = useRef(false);

  useLayoutEffect(() => {
    let cancelled = false;
    void loadRemoteState()
      .then((next) => {
        if (!cancelled) {
          writeCachedSiteContent(next.site);
          setState((s) => ({
            ...s,
            ...next,
            publicArticles: mergePublicArticlesPreferNewer(next.publicArticles, s.publicArticles),
            // categorias/site da API na 1ª carga; não apagar edições locais de artigos
            adminArticles: s.adminArticles,
            trashedArticles: s.trashedArticles,
          }));
          setHydrated(true);
          setSiteReady(true);
          prefetchStaffOnlineStatuses(
            next.site.staffMembers.map((m) => String(m.xatId ?? '').trim()).filter(Boolean)
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHydrated(true);
          setSiteReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefetch imediato a partir do cache local da equipa (antes da API do site).
  useEffect(() => {
    const members = cachedSiteRef.current?.staffMembers ?? state.site.staffMembers;
    prefetchStaffOnlineStatuses(
      members.map((m) => String(m.xatId ?? '').trim()).filter(Boolean)
    );
    // Só na montagem — evita refetch a cada edição de staff no painel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAdminGuides = async () => {
      if (!isPainelRoute || !hasApiAuthToken()) {
        if (!cancelled) {
          adminReadyRef.current = true;
          setState((s) => ({ ...s, adminArticles: [], trashedArticles: [] }));
          setAdminHydrated(true);
        }
        return;
      }

      // Não desidratar em refetch (ex.: refresh JWT ao voltar à aba).
      if (!adminReadyRef.current && !cancelled) setAdminHydrated(false);

      let adminArticles: CmsArticle[] = [];
      let trashedArticles: CmsArticle[] = [];

      try {
        adminArticles = await guidesService.getAdminGuides();
      } catch {
        adminArticles = [];
      }

      try {
        trashedArticles = await guidesService.getTrashedGuides();
      } catch {
        trashedArticles = [];
      }

      if (!cancelled) {
        setState((s) => ({
          ...s,
          adminArticles: mergeAdminArticlesPreferNewer(adminArticles, s.adminArticles),
          trashedArticles,
        }));
        adminReadyRef.current = true;
        setAdminHydrated(true);
      }
    };

    void loadAdminGuides();

    const onAuthChange = () => {
      void loadAdminGuides();
    };

    window.addEventListener('xat-auth-changed', onAuthChange);
    return () => {
      cancelled = true;
      window.removeEventListener('xat-auth-changed', onAuthChange);
    };
  }, [isPainelRoute]);

  const persistCategory = useCallback(async (action: 'create' | 'update' | 'delete', data: unknown) => {
    try {
      if (action === 'create') await guidesService.createCategory(data as Omit<CmsCategory, 'id'>);
      if (action === 'update') {
        const { id, ...patch } = data as { id: string } & Partial<CmsCategory>;
        await guidesService.updateCategory(id, patch);
      }
      if (action === 'delete') await guidesService.deleteCategory((data as { id: string }).id);
    } catch {
      // estado local mantém-se até API estar disponível
    }
  }, []);

  const addCategory = useCallback((input: Omit<CmsCategory, 'id'>) => {
    const row: CmsCategory = { ...input, id: crypto.randomUUID() };
    setState((s) => ({ ...s, categories: [...s.categories, row] }));
    void persistCategory('create', input);
    return row;
  }, [persistCategory]);

  const updateCategory = useCallback((id: string, patch: Partial<Omit<CmsCategory, 'id'>>) => {
    setState((s) => ({
      ...s,
      categories: s.categories.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
    void persistCategory('update', { id, ...patch });
  }, [persistCategory]);

  const deleteCategory = useCallback((id: string) => {
    if (HELP_CENTER_HOME_CATEGORY_IDS.has(id)) return;
    setState((s) => ({
      ...s,
      categories: s.categories.filter((c) => c.id !== id),
      publicArticles: s.publicArticles.filter((a) => a.categoryId !== id),
      adminArticles: s.adminArticles.filter((a) => a.categoryId !== id),
      trashedArticles: s.trashedArticles.filter((a) => a.categoryId !== id),
    }));
    void persistCategory('delete', { id });
  }, [persistCategory]);

  const addArticle = useCallback(
    async (input: Omit<CmsArticle, 'id' | 'createdAt' | 'updatedAt'>) => {
      const t = nowIso();
      const tempId = crypto.randomUUID();
      const row: CmsArticle = {
        ...input,
        id: tempId,
        createdAt: t,
        updatedAt: t,
      };
      setState((s) => ({
        ...s,
        adminArticles: [...s.adminArticles, row],
        publicArticles: syncPublishedArticle(s.publicArticles, row),
      }));

      try {
        const saved = await guidesService.createGuide(input);
        setState((s) => ({
          ...s,
          adminArticles: s.adminArticles.map((a) => (a.id === tempId ? saved : a)),
          publicArticles: syncPublishedArticle(
            s.publicArticles.filter((a) => a.id !== tempId),
            saved
          ),
        }));
        if (saved.status === 'published') {
          void analytics.cms(AnalyticsEventNames.CMS_PUBLISH, { guideId: saved.id });
        }
        return saved;
      } catch (err) {
        setState((s) => ({
          ...s,
          adminArticles: s.adminArticles.filter((a) => a.id !== tempId),
          publicArticles: s.publicArticles.filter((a) => a.id !== tempId),
        }));
        toast.error('Não foi possível criar o guia', {
          description: err instanceof ApiError ? err.message : 'Tente novamente.',
        });
        throw err;
      }
    },
    []
  );

  const updateArticle = useCallback(
    async (id: string, patch: Partial<Omit<CmsArticle, 'id' | 'createdAt'>>) => {
      const t = nowIso();
      let snapshot: CmsArticle | undefined;
      setState((s) => {
        snapshot = s.adminArticles.find((a) => a.id === id);
        const optimistic = snapshot ? { ...snapshot, ...patch, updatedAt: t } : null;
        return {
          ...s,
          adminArticles: s.adminArticles.map((a) =>
            a.id === id ? { ...a, ...patch, updatedAt: t } : a
          ),
          publicArticles: optimistic
            ? syncPublishedArticle(s.publicArticles, optimistic)
            : s.publicArticles,
        };
      });

      try {
        const saved = await guidesService.updateGuide(id, patch);
        setState((s) => ({
          ...s,
          adminArticles: s.adminArticles.map((a) => (a.id === id ? saved : a)),
          publicArticles: syncPublishedArticle(s.publicArticles, saved),
        }));
        if (patch.status === 'published' || saved.status === 'published') {
          void analytics.cms(AnalyticsEventNames.CMS_PUBLISH, { guideId: saved.id });
        } else {
          void analytics.cms(AnalyticsEventNames.GUIDE_UPDATED, { guideId: saved.id });
        }
      } catch (err) {
        if (snapshot) {
          setState((s) => ({
            ...s,
            adminArticles: s.adminArticles.map((a) => (a.id === id ? snapshot! : a)),
            publicArticles: syncPublishedArticle(s.publicArticles, snapshot!),
          }));
        }
        toast.error('Não foi possível guardar o guia', {
          description: err instanceof ApiError ? err.message : 'Tente novamente.',
        });
        throw err;
      }
    },
    []
  );

  const deleteArticle = useCallback((id: string) => {
    void analytics.cms(AnalyticsEventNames.CMS_DELETE, { guideId: id });
    setState((s) => {
      const victim = s.adminArticles.find((a) => a.id === id) ?? s.publicArticles.find((a) => a.id === id);
      const ref = victim?.sourceRef;
      const staticArticleExclusions = ref
        ? Array.from(new Set([...s.staticArticleExclusions, ref]))
        : s.staticArticleExclusions;
      const trashedRow = victim
        ? { ...victim, deletedAt: nowIso() }
        : null;
      return {
        ...s,
        publicArticles: s.publicArticles.filter((a) => a.id !== id),
        adminArticles: s.adminArticles.filter((a) => a.id !== id),
        trashedArticles: trashedRow
          ? [trashedRow, ...s.trashedArticles.filter((a) => a.id !== id)]
          : s.trashedArticles,
        staticArticleExclusions,
      };
    });

    void guidesService.deleteGuide(id).catch(() => undefined);
  }, []);

  const restoreArticle = useCallback((id: string) => {
    setState((s) => {
      const victim = s.trashedArticles.find((a) => a.id === id);
      if (!victim) return s;
      const restored = { ...victim, deletedAt: null };
      return {
        ...s,
        trashedArticles: s.trashedArticles.filter((a) => a.id !== id),
        adminArticles: [restored, ...s.adminArticles.filter((a) => a.id !== id)],
        publicArticles: syncPublishedArticle(s.publicArticles, restored),
      };
    });

    void guidesService
      .restoreGuide(id)
      .then((saved) => {
        setState((s) => ({
          ...s,
          adminArticles: s.adminArticles.map((a) => (a.id === id ? saved : a)),
          publicArticles: syncPublishedArticle(s.publicArticles, saved),
        }));
      })
      .catch(() => undefined);
  }, []);

  const permanentlyDeleteArticle = useCallback((id: string) => {
    setState((s) => ({
      ...s,
      trashedArticles: s.trashedArticles.filter((a) => a.id !== id),
    }));

    void guidesService.forceDeleteGuide(id).catch(() => undefined);
  }, []);

  const getCategoryById = useCallback(
    (id: string) => state.categories.find((c) => c.id === id),
    [state.categories]
  );

  const getArticleById = useCallback(
    (id: string) =>
      state.adminArticles.find((a) => a.id === id) ??
      state.publicArticles.find((a) => a.id === id),
    [state.adminArticles, state.publicArticles]
  );

  const panelArticles = isPainelRoute && hasApiAuthToken();
  const articles = panelArticles ? state.adminArticles : state.publicArticles;

  const siteRef = useRef(state.site);
  siteRef.current = state.site;
  const sitePersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sitePersistSeqRef = useRef(0);

  const schedulePersistSite = useCallback(() => {
    if (!isPainelRoute || !hasApiAuthToken()) return;
    if (sitePersistTimerRef.current) clearTimeout(sitePersistTimerRef.current);
    const seq = ++sitePersistSeqRef.current;
    sitePersistTimerRef.current = setTimeout(() => {
      const snapshot = siteRef.current;
      void siteContentService
        .updateSiteContent(snapshot)
        .then((saved) => {
          if (seq !== sitePersistSeqRef.current) return;
          const merged = mergeSiteWithDefaults(saved);
          writeCachedSiteContent(merged);
          setState((s) => ({
            ...s,
            site: merged,
          }));
        })
        .catch((err) => {
          toast.error('Não foi possível guardar as alterações do site', {
            description: err instanceof ApiError ? err.message : 'Tente novamente.',
          });
        });
    }, 700);
  }, [isPainelRoute]);

  useEffect(() => {
    return () => {
      if (sitePersistTimerRef.current) clearTimeout(sitePersistTimerRef.current);
    };
  }, []);

  const setSite = useCallback(
    (site: SiteContent, options?: { persist?: boolean }) => {
      siteRef.current = site;
      writeCachedSiteContent(site);
      setState((s) => ({ ...s, site }));
      setSiteReady(true);
      if (options?.persist === false) return;
      schedulePersistSite();
    },
    [schedulePersistSite]
  );

  const patchSiteHero = useCallback(
    (patch: Partial<SiteHero>) => {
      setState((s) => {
        const site = { ...s.site, hero: { ...s.site.hero, ...patch } };
        siteRef.current = site;
        return { ...s, site };
      });
      schedulePersistSite();
    },
    [schedulePersistSite]
  );

  const setRulesSections = useCallback(
    (sections: SiteRulesSection[]) => {
      setState((s) => {
        const site = { ...s.site, rulesSections: sections };
        siteRef.current = site;
        return { ...s, site };
      });
      schedulePersistSite();
    },
    [schedulePersistSite]
  );

  const setConduct = useCallback(
    (conduct: SiteConductBlock) => {
      setState((s) => {
        const site = { ...s.site, conduct };
        siteRef.current = site;
        return { ...s, site };
      });
      schedulePersistSite();
    },
    [schedulePersistSite]
  );

  const setStaffMembers = useCallback(
    (members: SiteStaffMember[]) => {
      setState((s) => {
        const site = { ...s.site, staffMembers: members };
        siteRef.current = site;
        return { ...s, site };
      });
      schedulePersistSite();
    },
    [schedulePersistSite]
  );

  const addStaffMember = useCallback(
    (input: Omit<SiteStaffMember, 'id'>) => {
      const row: SiteStaffMember = { ...input, id: crypto.randomUUID() };
      setState((s) => {
        const site = { ...s.site, staffMembers: [...s.site.staffMembers, row] };
        siteRef.current = site;
        return { ...s, site };
      });
      schedulePersistSite();
      return row;
    },
    [schedulePersistSite]
  );

  const updateStaffMember = useCallback(
    (id: string, patch: Partial<Omit<SiteStaffMember, 'id'>> & { xatRank?: SiteStaffXatRank | null }) => {
      setState((s) => {
        const site = {
          ...s.site,
          staffMembers: s.site.staffMembers.map((m) => {
            if (m.id !== id) return m;
            const next = { ...m, ...patch };
            if ('xatRank' in patch && (patch.xatRank === null || patch.xatRank === undefined)) {
              const { xatRank: _removed, ...rest } = next;
              return rest;
            }
            return next;
          }),
        };
        siteRef.current = site;
        return { ...s, site };
      });
      schedulePersistSite();
    },
    [schedulePersistSite]
  );

  const deleteStaffMember = useCallback(
    (id: string) => {
      setState((s) => {
        const site = {
          ...s.site,
          staffMembers: s.site.staffMembers.filter((m) => m.id !== id),
        };
        siteRef.current = site;
        return { ...s, site };
      });
      schedulePersistSite();
    },
    [schedulePersistSite]
  );

  const value = useMemo<CmsContextValue>(
    () => ({
      categories: state.categories,
      articles,
      trashedArticles: state.trashedArticles,
      site: state.site,
      staticArticleExclusions: state.staticArticleExclusions,
      hydrated,
      siteReady,
      adminHydrated,
      addCategory,
      updateCategory,
      deleteCategory,
      addArticle,
      updateArticle,
      deleteArticle,
      restoreArticle,
      permanentlyDeleteArticle,
      getCategoryById,
      getArticleById,
      setSite,
      patchSiteHero,
      setRulesSections,
      setConduct,
      setStaffMembers,
      addStaffMember,
      updateStaffMember,
      deleteStaffMember,
    }),
    [
      state.categories,
      state.site,
      state.staticArticleExclusions,
      articles,
      state.trashedArticles,
      hydrated,
      siteReady,
      adminHydrated,
      addCategory,
      updateCategory,
      deleteCategory,
      addArticle,
      updateArticle,
      deleteArticle,
      restoreArticle,
      permanentlyDeleteArticle,
      getCategoryById,
      getArticleById,
      setSite,
      patchSiteHero,
      setRulesSections,
      setConduct,
      setStaffMembers,
      addStaffMember,
      updateStaffMember,
      deleteStaffMember,
    ]
  );

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms() {
  const ctx = useContext(CmsContext);
  if (!ctx) {
    throw new Error('useCms must be used within CmsProvider');
  }
  return ctx;
}
